import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import {
  useRespostasRapidas, criarResposta, atualizarResposta, excluirResposta,
  VARIAVEIS_RESPOSTA, resolverVariaveis,
} from '../helpers/respostasRapidas';

// Mensagens prontas do atendente. São DOIS caminhos pro mesmo lugar:
//   - o raio (⚡): abre a lista, pra quem não decorou nada
//   - digitar /atalho no campo: pra quem já sabe qual quer
// Tudo que aparece aqui já vem com as variáveis resolvidas ({nome} -> Pedro),
// pra atendente ler a mensagem final e não um texto com marcador.
//
// props:
//   value        texto atual do campo (pra detectar o gatilho "/")
//   onInserir    recebe o texto JÁ resolvido
//   inputRef     ref do textarea — usada pra navegar a lista pelo teclado
//   variaveis    { nomeCliente, atendente } pra resolver e pra prévia

const VERDE = '#3D8B37';

// Painel/dropdown flutuante: `position: fixed` ancorado no botão. Dentro de um
// modal, `absolute` era cortado pelo overflow do modal (o texto sumia).
function Flutuante({ ancora, largura = 320, children }) {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    const calcular = () => {
      const el = ancora?.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const alturaMax = 320;
      // Abre pra cima quando tem espaço; senão pra baixo (campo no topo da tela).
      const cabeAcima = r.top > alturaMax + 16;
      setPos({
        left: Math.max(8, Math.min(r.left, window.innerWidth - largura - 8)),
        top: cabeAcima ? undefined : r.bottom + 6,
        bottom: cabeAcima ? window.innerHeight - r.top + 6 : undefined,
      });
    };
    calcular();
    window.addEventListener('resize', calcular);
    window.addEventListener('scroll', calcular, true);
    return () => {
      window.removeEventListener('resize', calcular);
      window.removeEventListener('scroll', calcular, true);
    };
  }, [ancora, largura]);
  if (!pos) return null;
  return createPortal(
    <div style={{
      position: 'fixed', left: pos.left, top: pos.top, bottom: pos.bottom,
      width: largura, maxHeight: 320, overflowY: 'auto', zIndex: 3000,
      background: 'var(--portal-surface)', border: '1px solid var(--portal-border)',
      borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
    }}>
      {children}
    </div>,
    document.body,
  );
}

// Editor de mensagem pronta: atalho, texto e os chips de variável — clicar
// insere na posição do cursor, que é como se escreve de verdade.
function EditorMensagem({ aberto, inicial, ctx, onFechar, onSalvo }) {
  const [atalho, setAtalho] = useState('');
  const [texto, setTexto] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const areaRef = useRef(null);

  useEffect(() => {
    if (!aberto) return;
    setAtalho(inicial?.atalho || '');
    setTexto(inicial?.texto || '');
    setErro('');
  }, [aberto, inicial]);

  if (!aberto) return null;

  const inserirVariavel = (chave) => {
    const el = areaRef.current;
    const ini = el?.selectionStart ?? texto.length;
    const fim = el?.selectionEnd ?? texto.length;
    const novo = `${texto.slice(0, ini)}${chave}${texto.slice(fim)}`;
    setTexto(novo);
    // devolve o cursor logo depois do que foi inserido
    setTimeout(() => {
      if (!el) return;
      el.focus();
      const p = ini + chave.length;
      el.setSelectionRange(p, p);
    }, 20);
  };

  const salvar = async () => {
    const a = atalho.trim().replace(/^\/+/, '').replace(/\s+/g, '');
    const t = texto.trim();
    if (a.length < 2) { setErro('O atalho precisa de pelo menos 2 letras.'); return; }
    if (!t) { setErro('Escreva o texto da mensagem.'); return; }
    setSalvando(true);
    setErro('');
    try {
      if (inicial?.id) await atualizarResposta(inicial.id, a, t);
      else await criarResposta(a, t);
      onSalvo?.();
      onFechar();
    } catch (e) {
      setErro(e.response?.data?.message || 'Não deu pra salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const previa = resolverVariaveis(texto, ctx) || '(vazio)';

  return createPortal(
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', inset: 0, zIndex: 3200, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto',
          background: 'var(--portal-surface)', color: 'var(--portal-text)',
          borderRadius: 12, boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{
          padding: '14px 18px', borderBottom: '1px solid var(--portal-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <strong>
            <i className={`bi ${inicial?.id ? 'bi-pencil' : 'bi-lightning-charge'} me-2`} />
            {inicial?.id ? 'Editar mensagem pronta' : 'Nova mensagem pronta'}
          </strong>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onFechar}>
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {erro && <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.85rem' }}>{erro}</div>}

          <div>
            <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
              Atalho — é o que você digita no campo pra chamar esta mensagem
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: '1.1rem', fontWeight: 700, color: VERDE }}>/</span>
              <input
                className="form-control"
                value={atalho}
                onChange={(e) => setAtalho(e.target.value)}
                placeholder="cobranca"
                autoFocus
              />
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)', marginTop: 3 }}>
              Sem espaços. Depois é só digitar <b>/{atalho.trim() || 'cobranca'}</b> na conversa.
            </div>
          </div>

          <div>
            <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
              Mensagem
            </label>
            <textarea
              ref={areaRef}
              className="form-control"
              rows={4}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Olá {nome}, tudo bem? Passando pra lembrar do seu boleto..."
            />
          </div>

          <div>
            <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', marginBottom: 6 }}>
              Clique pra inserir onde o cursor está — o Fio troca pelo valor real na hora de usar:
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {VARIAVEIS_RESPOSTA.map((v) => (
                <button
                  key={v.chave}
                  type="button"
                  onClick={() => inserirVariavel(v.chave)}
                  title={v.ajuda}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 10px', borderRadius: 14, cursor: 'pointer',
                    border: `1px solid ${VERDE}66`, background: 'rgba(61, 139, 55, 0.12)',
                    color: 'var(--portal-text)', fontSize: '0.78rem',
                  }}
                >
                  <i className="bi bi-plus-lg" style={{ color: VERDE }} />
                  <b>{v.chave}</b>
                  <span style={{ color: 'var(--portal-muted)' }}>{v.rotulo}</span>
                </button>
              ))}
            </div>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: '0.74rem', color: 'var(--portal-muted)', lineHeight: 1.5 }}>
              {VARIAVEIS_RESPOSTA.map((v) => (
                <li key={v.chave}><b>{v.chave}</b> — {v.ajuda}</li>
              ))}
            </ul>
          </div>

          <div style={{
            padding: 12, borderRadius: 10, border: '1px solid var(--portal-border)',
            background: 'var(--portal-surface-soft)',
          }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)', marginBottom: 4 }}>
              <i className="bi bi-eye me-1" />Como vai sair
              {ctx?.nomeCliente ? ` (com ${ctx.nomeCliente})` : ' (sem cliente escolhido)'}
            </div>
            <div style={{ fontSize: '0.88rem', whiteSpace: 'pre-wrap' }}>{previa}</div>
          </div>
        </div>

        <div style={{
          padding: '12px 18px', borderTop: '1px solid var(--portal-border)',
          display: 'flex', justifyContent: 'space-between', gap: 8,
        }}>
          <div>
            {inicial?.id && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                disabled={salvando}
                onClick={async () => {
                  const r = await Swal.fire({
                    icon: 'warning', title: 'Excluir esta mensagem?',
                    text: `/${inicial.atalho}`, showCancelButton: true,
                    confirmButtonText: 'Excluir', cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#b43737',
                  });
                  if (!r.isConfirmed) return;
                  await excluirResposta(inicial.id);
                  onSalvo?.();
                  onFechar();
                }}
              >
                Excluir
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onFechar} disabled={salvando}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={salvar}
              disabled={salvando}
              style={{ background: VERDE, color: '#fff', border: 'none' }}
            >
              <i className="bi bi-check2 me-1" />{salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function RespostasRapidas({ value, onInserir, inputRef, variaveis }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [editor, setEditor] = useState(null); // null = fechado; {} = nova; {id...} = editar
  const [idx, setIdx] = useState(0);          // item destacado na navegação por teclado
  const lista = useRespostasRapidas();
  const botaoRef = useRef(null);
  const ctx = useMemo(() => ({
    nomeCliente: variaveis?.nomeCliente || '',
    atendente: variaveis?.atendente || '',
  }), [variaveis?.nomeCliente, variaveis?.atendente]);

  // Gatilho "/": enquanto o campo tem só "/algo", sugere as mensagens.
  const trigger = /^\/(\S*)$/.exec(value || '');
  const termo = trigger ? trigger[1].toLowerCase() : null;
  const sugestoes = useMemo(
    () => (termo != null ? lista.filter((r) => r.atalho.toLowerCase().startsWith(termo)) : []),
    [termo, lista],
  );
  const mostrarSugestoes = termo != null && sugestoes.length > 0;

  useEffect(() => { setIdx(0); }, [termo]);

  const usar = (r) => {
    onInserir(resolverVariaveis(r.texto, ctx));
    setAberto(false);
    setBusca('');
  };

  // Teclado no CAMPO enquanto a sugestão está aberta: ↑↓ escolhe, Enter/Tab usa,
  // Esc fecha. Sem isso o "/atalho" ficava sem saída — era preciso clicar.
  useEffect(() => {
    const el = inputRef?.current;
    if (!el || !mostrarSugestoes) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIdx((i) => (i + 1) % sugestoes.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setIdx((i) => (i - 1 + sugestoes.length) % sugestoes.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        usar(sugestoes[Math.min(idx, sugestoes.length - 1)]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onInserir('');
      }
    };
    el.addEventListener('keydown', onKey, true);
    return () => el.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef, mostrarSugestoes, sugestoes, idx, ctx]);

  // Fecha o painel ao clicar fora.
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e) => {
      if (botaoRef.current && !botaoRef.current.contains(e.target)) {
        // cliques dentro do painel (portal) têm o marcador abaixo
        if (!e.target.closest?.('[data-rr-painel]')) setAberto(false);
      }
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const itemStyle = {
    display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%',
    padding: '9px 10px', background: 'transparent', border: 'none',
    borderBottom: '1px solid var(--portal-border)', color: 'var(--portal-text)',
    textAlign: 'left', cursor: 'pointer', fontSize: '0.82rem',
  };

  const filtradas = busca.trim()
    ? lista.filter((r) => `${r.atalho} ${r.texto}`.toLowerCase().includes(busca.trim().toLowerCase()))
    : lista;

  return (
    <div ref={botaoRef} style={{ position: 'relative', flexShrink: 0 }}>
      {/* Sugestão do "/" — com o item destacado e a dica de teclado. */}
      {mostrarSugestoes && (
        <Flutuante ancora={botaoRef} largura={340}>
          <div data-rr-painel style={{
            padding: '7px 10px', fontSize: '0.72rem', color: 'var(--portal-muted)',
            borderBottom: '1px solid var(--portal-border)',
          }}>
            Mensagens que começam com <b>/{termo}</b>
          </div>
          {sugestoes.map((r, i) => (
            <button
              key={r.id}
              data-rr-painel
              type="button"
              style={{ ...itemStyle, background: i === idx ? 'rgba(61, 139, 55, 0.16)' : 'transparent' }}
              onMouseEnter={() => setIdx(i)}
              onClick={() => usar(r)}
            >
              <span style={{ color: VERDE, fontWeight: 700, whiteSpace: 'nowrap' }}>/{r.atalho}</span>
              <span style={{ flex: 1, opacity: 0.85 }}>{resolverVariaveis(r.texto, ctx)}</span>
            </button>
          ))}
          <div data-rr-painel style={{
            padding: '7px 10px', fontSize: '0.7rem', color: 'var(--portal-muted)',
            background: 'var(--portal-surface-soft)',
          }}>
            <b>↑ ↓</b> escolher · <b>Enter</b> ou <b>Tab</b> usar · <b>Esc</b> sair
          </div>
        </Flutuante>
      )}

      {/* Painel do raio: lista + busca + criar. */}
      {aberto && !mostrarSugestoes && (
        <Flutuante ancora={botaoRef} largura={340}>
          <div data-rr-painel style={{ padding: 10, borderBottom: '1px solid var(--portal-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
              <strong style={{ fontSize: '0.85rem' }}>
                <i className="bi bi-lightning-charge me-1" style={{ color: VERDE }} />Mensagens prontas
              </strong>
              <button
                type="button"
                className="btn btn-sm"
                style={{ background: VERDE, color: '#fff', border: 'none', fontSize: '0.76rem' }}
                onClick={() => setEditor({})}
              >
                <i className="bi bi-plus-lg me-1" />Nova
              </button>
            </div>
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar mensagem..."
              style={{
                width: '100%', padding: '6px 9px', borderRadius: 6, fontSize: '0.82rem',
                border: '1px solid var(--portal-border)', background: 'var(--portal-bg)',
                color: 'var(--portal-text)',
              }}
            />
            <div style={{ fontSize: '0.71rem', color: 'var(--portal-muted)', marginTop: 6 }}>
              Clique pra usar. Atalho: digite <b>/nome-do-atalho</b> direto no campo.
            </div>
          </div>

          {filtradas.length === 0 ? (
            <div data-rr-painel style={{ padding: 14, fontSize: '0.82rem', color: 'var(--portal-muted)' }}>
              {lista.length === 0
                ? 'Nenhuma mensagem pronta ainda. Crie a primeira em "Nova" — ex.: /cobranca.'
                : 'Nada encontrado com esse termo.'}
            </div>
          ) : filtradas.map((r) => (
            <div data-rr-painel key={r.id} style={{ display: 'flex', alignItems: 'stretch' }}>
              <button type="button" style={{ ...itemStyle, flex: 1 }} onClick={() => usar(r)}>
                <span style={{ color: VERDE, fontWeight: 700, whiteSpace: 'nowrap' }}>/{r.atalho}</span>
                <span style={{ flex: 1, opacity: 0.85 }}>{resolverVariaveis(r.texto, ctx)}</span>
              </button>
              <button
                type="button"
                title="Editar"
                onClick={() => setEditor(r)}
                style={{
                  border: 'none', borderBottom: '1px solid var(--portal-border)',
                  background: 'transparent', color: 'var(--portal-muted)',
                  padding: '0 10px', cursor: 'pointer',
                }}
              >
                <i className="bi bi-pencil" />
              </button>
            </div>
          ))}
        </Flutuante>
      )}

      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        title="Mensagens prontas (ou digite /atalho no campo)"
        style={{
          height: 38, width: 38, flexShrink: 0,
          background: 'var(--portal-surface)', border: '1px solid var(--portal-border)',
          color: aberto ? VERDE : 'var(--portal-text)',
          borderRadius: 8, cursor: 'pointer',
        }}
      >
        <i className="bi bi-lightning-charge" />
      </button>

      <EditorMensagem
        aberto={!!editor}
        inicial={editor}
        ctx={ctx}
        onFechar={() => setEditor(null)}
        onSalvo={() => setBusca('')}
      />
    </div>
  );
}
